import React, { useEffect, useState } from "react";
import AppShell from "../../components/AppShell";
import StatusBadge from "../../components/StatusBadge";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Checkbox } from "../../components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "../../components/ui/radio-group";
import { FileText, Check, Download, Camera } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, API } from "../../lib/api";

export default function Forms() {
  const [templates, setTemplates] = useState([]);
  const [responses, setResponses] = useState([]);
  const [active, setActive] = useState(null);
  const [signature, setSignature] = useState("");
  const [ack, setAck] = useState(false);
  const [mediaChoice, setMediaChoice] = useState("authorize");
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/form-templates").then((r) => setTemplates(r.data)).catch(() => {});
    api.get("/form-responses/me").then((r) => setResponses(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const annual = templates.filter((t) => t.category !== "event");
  const respByKey = (key) => responses.find((r) => r.template_key === key && !r.experience_id);

  const openSign = (t) => { setActive(t); setSignature(""); setAck(false); setMediaChoice("authorize"); };

  const sign = async () => {
    if (!signature.trim() || !ack) { toast.error("Type your full name and confirm your intent to sign electronically."); return; }
    setSaving(true);
    try {
      await api.post("/form-responses", {
        template_id: active.id, signature, acknowledged: ack,
        media_choice: active.key === "photo_media" ? mediaChoice : null,
      });
      toast.success("Document signed and stored.");
      setActive(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const download = (resp) => {
    const token = localStorage.getItem("hea_token");
    const url = `${API}/form-responses/${resp.id}/document`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.text())
      .then((html) => {
        const blobUrl = URL.createObjectURL(new Blob([html], { type: "text/html" }));
        window.open(blobUrl, "_blank", "noopener,noreferrer");
        setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
      })
      .catch(() => toast.error("Could not open document."));
  };

  return (
    <AppShell>
      <h1 className="font-display text-3xl font-bold text-navy">Required Forms</h1>
      <p className="text-slateBrand">Sign annual agreements once. Event-specific waivers are completed during each registration.</p>

      <h2 className="mt-8 font-display text-lg font-bold text-navy">Required Annual Forms</h2>
      <div className="mt-4 space-y-3">
        {annual.map((t) => {
          const resp = respByKey(t.key);
          const signed = !!resp;
          return (
            <div key={t.id} data-testid={`form-template-${t.id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-5">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brandblue/10">{t.key === "photo_media" ? <Camera className="h-5 w-5 text-brandblue" /> : <FileText className="h-5 w-5 text-brandblue" />}</div>
                <div>
                  <div className="font-display font-semibold text-navy">{t.name}</div>
                  <div className="text-xs text-slateBrand">Annual / reusable • v{t.version}{signed ? ` • Signed ${new Date(resp.signed_at).toLocaleDateString()}` : ""}{resp?.media_choice ? ` • ${resp.media_choice === "authorize" ? "Media authorized" : "Media declined"}` : ""}</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <StatusBadge status={signed ? "complete" : "missing"} label={signed ? "Signed" : "Action Required"} />
                {t.pdf_url && (
                  <a href={t.pdf_url} target="_blank" rel="noopener noreferrer" data-testid={`form-pdf-${t.id}`}
                    className="inline-flex items-center gap-1 text-sm font-semibold text-brandblue hover:underline"><FileText className="h-4 w-4" />PDF</a>
                )}
                {signed ? (
                  <Button size="sm" variant="outline" data-testid={`form-download-${t.id}`} onClick={() => download(resp)}><Download className="mr-1 h-4 w-4" />Download</Button>
                ) : (
                  <Button size="sm" data-testid={`form-sign-btn-${t.id}`} onClick={() => openSign(t)} className="bg-brandblue font-display font-semibold hover:bg-blue-700">Review & Sign</Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-xl text-navy">{active?.name}</DialogTitle></DialogHeader>
          {active?.pdf_url && (
            <a href={active.pdf_url} target="_blank" rel="noopener noreferrer" data-testid="form-pdf-link"
              className="inline-flex items-center gap-2 text-sm font-semibold text-brandblue hover:underline">
              <FileText className="h-4 w-4" />Open / download the original PDF
            </a>
          )}
          <div className="max-h-64 overflow-y-auto whitespace-pre-line rounded-lg bg-cloud p-4 text-sm leading-relaxed text-slateBrand">{active?.body}</div>
          <div className="space-y-4">
            {active?.key === "photo_media" && (
              <div>
                <Label className="font-display font-semibold text-navy">Media authorization</Label>
                <RadioGroup value={mediaChoice} onValueChange={setMediaChoice} className="mt-2 space-y-2">
                  <label className="flex items-center gap-3 rounded-lg border border-slate-200 p-3 text-sm text-navy" data-testid="media-authorize"><RadioGroupItem value="authorize" />I AUTHORIZE photo / media use</label>
                  <label className="flex items-center gap-3 rounded-lg border border-slate-200 p-3 text-sm text-navy" data-testid="media-decline"><RadioGroupItem value="decline" />I DO NOT AUTHORIZE photo / media use</label>
                </RadioGroup>
              </div>
            )}
            <div>
              <Label className="font-display font-semibold text-navy">Type your full legal name to sign</Label>
              <Input data-testid="form-signature-input" className="mt-1.5 h-11" value={signature} onChange={(e) => setSignature(e.target.value)} placeholder="Your full name" />
            </div>
            <label className="flex items-start gap-3 text-sm text-navy" data-testid="form-ack-check">
              <Checkbox checked={ack} onCheckedChange={setAck} className="mt-0.5" />
              <span>I intend this to be my electronic signature with the same force and effect as my handwritten signature, and I certify I am the parent/legal guardian.</span>
            </label>
            <Button data-testid="form-submit-btn" disabled={saving} onClick={sign} className="h-12 w-full bg-brandblue font-display font-semibold hover:bg-blue-700"><Check className="mr-2 h-4 w-4" />Sign Electronically</Button>
          </div>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
